import { Fragment, useCallback, useMemo, useState } from 'react';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import {
  FiltersQualifications,
  // QualificationMenuItems,
  // QualificationOperatorModal,
} from 'system/components/qualifications/list';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { useEffect } from 'react';
import { Qualification } from 'shell/models/qualification';
import { FiltersFormQualification, QualificationClient } from './types';
import { useFormik } from 'formik';
import { transformDataset } from './utils';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesQualification } from '..';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { generatePath, useNavigate } from 'react-router-dom';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonQualificationCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { authsObj, headCells, validationSchemaQualificationsFilter } from './constants';
import { Typography, useEventCallback } from '@mui/material';
import ListHeader from 'shell/components/shared/list-header';

interface OperatorModal<T> extends AlertModal<T> {
  choosenElement?: string;
}

const QualificationsPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<OperatorModal<QualificationClient>>({
    element: null,
    open: false,
    choosenElement: '',
  });

  // const [operatorModal, setOperatorModal] = useState<OperatorModal<QualificationClient>>({
  //   element: null,
  //   open: false,
  //   choosenElement: '',
  // });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<QualificationClient>>({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<FiltersFormQualification>();
  const {
    data,
    get: getQualifications,
    error,
  } = useHttpFetch<HttpResponseList<Qualification>>(`${__MANAGEMENT_API_URL__}/qualifiche`, {
    data: {
      payload: {
        list: [],
      },
    },
  });

  // const { get: getOperatorsByQualification } = useHttpFetch<HttpResponseList<Operator>>(
  //   `${__MANAGEMENT_API_URL__}/operatori/qualifiche`,
  //   {
  //     data: {},
  //   }
  // );

  const { delete: deleteQualification } = useHttpFetch<HttpResponse<Qualification>>(
    `${__MANAGEMENT_API_URL__}/qualifiche`
  );

  const qualifications: Array<QualificationClient> = useMemo(() => {
    if (!!error || !data?.payload) {
      return [];
    }
    return data.payload.list.map((qualification) => ({
      code: qualification.key.codiceQualifica,
      description: qualification.descrizione,
    }));
  }, [data]);

  const onSubmit = useEventCallback((formValues: FiltersFormQualification) => {
    const queryString = transformDataset({ ...formValues, page: 0 });
    filterQualificationForm.setFieldValue('page', 0);
    getQualifications(queryString);
  });

  const filterQualificationForm = useFormik({
    initialValues: initialValuesQualification,
    validationSchema: validationSchemaQualificationsFilter,
    onSubmit,
  });
  useEffect(() => {
    filterQualificationForm.setValues(filterTable.values || initialValuesQualification);
    const queryString = transformDataset(filterTable.values || initialValuesQualification);
    getQualifications(queryString);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterQualificationForm.values);
  }, [filterQualificationForm.values]);

  const count: number = useMemo(() => {
    if (!!error || !data?.payload || !data?.payload.pageInfo) {
      return 0;
    }
    return data.payload.pageInfo?.totalCount;
  }, [error, data?.payload]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useCallback(
    (cell): Promise<QualificationClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/qualifiche/dettaglio/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<QualificationClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/qualifiche/modifica/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useEventCallback(
    (cell: QualificationClient): Promise<QualificationClient> => {
      return new Promise((resolve) => {
        // getOperatorsByQualification(`/${cell.code}`).then((response) => {
        //   const serviceResponse = response as HttpResponseList<Operator>;
        //   if (serviceResponse.payload.list.length > 0) {
        //     setOperatorModal({ open: true, element: cell, choosenElement: '' });
        //     return;
        //   }
        setDeleteAlertModal({ open: true, element: cell, choosenElement: '' });
        // });
        resolve(cell);
      }) as Promise<QualificationClient>;
    }
  );

  const handleChangePage = useEventCallback((newPage: number) => {
    filterQualificationForm.setFieldValue('page', newPage);
  });

  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    const newDataset = {
      ...filterQualificationForm.values,
      rowsPerPage: rowsPerPage || 0,
      page: 0,
    };
    filterQualificationForm.setValues(newDataset);
  });

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterQualificationForm.values, orders };
      const queryString = transformDataset(newDataset);
      return getQualifications(queryString).then(() => {
        filterQualificationForm.setFieldValue('orders', orders);
      });
    },
    [filterQualificationForm.setFieldValue, getQualifications, filterQualificationForm.values]
  );

  const onCloseDeleteAlertModal = useEventCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  });

  const onYesDeleteAlertModal = useEventCallback(() => {
    const newCodiceQualifica = !deleteAlertModal.choosenElement
      ? ''
      : `?newCodiceQualifica=${deleteAlertModal.choosenElement}`;
    deleteQualification(`/${deleteAlertModal.element?.code}${newCodiceQualifica}`).then(() => {
      setDeleteAlertModal({ open: false, element: null });
      setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
    });
  });

  const onNoDeleteAlertModal = useEventCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  });

  // const onCloseQualificationOperatorModal = useEventCallback(() => {
  //   setOperatorModal({ open: false, element: null });
  // });

  // const onYesQualificationOperatorModal = useEventCallback(() => {
  //   setDeleteAlertModal({
  //     open: true,
  //     element: operatorModal.element,
  //     choosenElement: operatorModal.choosenElement,
  //   });
  //   setOperatorModal({ open: false, element: null, choosenElement: '' });
  // });

  // const onNoQualificationOperatorModal = useCallback(() => {
  //   setOperatorModal({ open: false, element: null });
  // }, [setOperatorModal]);

  // const itemsQualificationMenu: Array<ItemVM> = useMemo(() => {
  //   if (!qualifications) {
  //     return [];
  //   }
  //   return qualifications
  //     .filter((qualification) => qualification.code !== operatorModal.element?.code)
  //     .map(
  //       (qualification) =>
  //         ({
  //           id: qualification.code,
  //           value: qualification.description,
  //         } as ItemVM)
  //     );
  // }, [qualifications]);

  // const onChangeQualificationMenuItems = useEventCallback((event: SelectChangeEvent<string>) => {
  //   const newValue = event.target.value as string;
  //   setOperatorModal({ ...(operatorModal || {}), choosenElement: newValue });
  // });

  const onCloseDeleteSuccessModal = useEventCallback(() => {
    const queryString = transformDataset(filterQualificationForm.values);
    getQualifications(queryString).then(() =>
      setDeleteSuccessModal({ open: false, element: null })
    );
  });

  const onOkDeleteSuccessModal = useEventCallback(() => {
    const queryString = transformDataset(filterQualificationForm.values);
    getQualifications(queryString).then(() =>
      setDeleteSuccessModal({ open: false, element: null })
    );
  });

  return (
    <Fragment>
      <Breadcrumbs items={CommonQualificationCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuova qualifica"
        addBtnLabel="Aggiungi nuova qualifica"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/qualifiche/nuovo');
        }}
        title="Gestione Qualifiche"
      />
      <div className="pmi-my-3">
        <FiltersQualifications filterQualificationForm={filterQualificationForm} />
      </div>
      {data?.payload ? (
        qualifications?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterQualificationForm.values.orders}
            activeActions={auths}
            page={filterQualificationForm.values.page}
            rowsPerPage={filterQualificationForm.values.rowsPerPage}
            body={qualifications}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Servizi"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Qualifica"
        message={`Confermare la cancellazione della qualifica ${
          deleteAlertModal.element?.description || ''
        }?`}
        open={deleteAlertModal.open}
        onClose={onCloseDeleteAlertModal}
        onYes={onYesDeleteAlertModal}
        onNo={onNoDeleteAlertModal}
      />
      {/* <QualificationOperatorModal
        open={operatorModal.open}
        onClose={onCloseQualificationOperatorModal}
        onYes={onYesQualificationOperatorModal}
        onNo={onNoQualificationOperatorModal}
        disabled={!operatorModal.choosenElement}
      >
        <QualificationMenuItems
          items={itemsQualificationMenu}
          value={operatorModal.choosenElement || ''}
          onChange={onChangeQualificationMenuItems}
        />
      </QualificationOperatorModal> */}
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Qualifica"
        message={`La cancellazione della qualifica ${
          deleteSuccessModal.element?.description || ''
        } è avvenuta con successo`}
        onClose={onCloseDeleteSuccessModal}
        onOk={onOkDeleteSuccessModal}
      />
    </Fragment>
  );
};

QualificationsPage.displayName = 'Qualifications page component';

export default QualificationsPage;
